import { useState, useRef, useEffect } from 'react';
import { LogOut, User as UserIcon, ChevronDown, LogIn, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth.jsx';
import AuthModal from './AuthModal.jsx';
import { cn } from '../lib/cn.js';

function avatarLetter(user) {
  const seed = (user?.email || user?.user_metadata?.full_name || 'U').trim();
  return seed.charAt(0).toUpperCase();
}

function displayName(user) {
  return (
    user?.user_metadata?.name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    '匿名用户'
  );
}

export default function UserMenu() {
  const { user, loading, isSupabaseEnabled, signOut } = useAuth();
  const [openMenu, setOpenMenu] = useState(false);
  const [openAuth, setOpenAuth] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpenMenu(false);
      }
    }
    if (openMenu) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openMenu]);

  // Supabase 没配置时整个 UI 不渲染（保持「纯本地模式」干净）
  if (!isSupabaseEnabled) return null;

  if (loading) {
    return (
      <div className="inline-flex h-9 w-9 items-center justify-center text-ink-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpenAuth(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 shadow-sm transition hover:bg-ink-50"
        >
          <LogIn className="h-3.5 w-3.5" />
          登录
        </button>
        <AuthModal open={openAuth} onOpenChange={setOpenAuth} />
      </>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpenMenu((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 text-sm transition hover:bg-ink-100"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-accent-500 text-xs font-semibold text-white">
          {avatarLetter(user)}
        </span>
        <span className="hidden max-w-[140px] truncate text-ink-700 sm:inline">
          {displayName(user)}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
      </button>

      {openMenu && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 w-56 rounded-xl border border-ink-200 bg-white py-1.5',
            'shadow-lg ring-1 ring-ink-900/5',
          )}
        >
          <div className="border-b border-ink-100 px-3 py-2">
            <div className="truncate text-sm font-medium text-ink-900">
              {displayName(user)}
            </div>
            <div className="truncate text-xs text-ink-400">{user.email}</div>
          </div>
          <a
            href="/profile"
            className="flex items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
            onClick={() => setOpenMenu(false)}
          >
            <UserIcon className="h-4 w-4" />
            我的简历 / 偏好
          </a>
          <button
            type="button"
            onClick={async () => {
              setOpenMenu(false);
              await signOut();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
          >
            <LogOut className="h-4 w-4" />
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}
